<template>
    <div id="manage-invite-user" class="flex flex-col w-full h-full p-4 bg-white overflow-hidden">
        <div class="flex justify-between items-center h-10">
            <div class="flex items-center">
                <h1 class="m-0">{{ $t('manage_school_users_invite_header') }}</h1>
            </div>
            <div @click="$emit('close')" class="manage-close-button flex justify-center items-center">
                <i class="fas fa-times text-xl shadow-text-none cursor-pointer"></i>
            </div>
        </div>
        <div class="manage-invite-content flex-1 flex flex-col overflow-hidden">
            <div>
                <form
                    @submit.prevent="add()"
                    class="manage-invite-input-box flex flex-col md:flex-row items-center w-full"
                >
                    <input
                        v-model="email"
                        @input="alreadyInvited = false"
                        :placeholder="$t('manage_school_users_invite_placeholder_enter_email')"
                        type="email"
                        class="w-full"
                        name="invite-email"
                    />

                    <XButton
                        :disabled="!emailSyntax || loading || inviteUsers.length >= limitEmail"
                        type="submit"
                        class="manage-invite-button system-button-color system-button-text-color w-full md:w-auto shadow-text-none"
                    >
                        {{ $t('manage_school_users_invite_button_add') }}
                    </XButton>
                </form>
                <span v-if="alreadyInvited" class="block mt-1 font-size-16 text-red">
                    <i class="fas fa-exclamation-circle"></i>
                    {{
                        email === you.email
                            ? $t('manage_school_users_invite_email_cannot_add_own_email')
                            : $t('manage_school_users_invite_email_already_invited')
                    }}
                </span>
            </div>
            <ManageTable :list="inviteUsers" :paginator="false">
                <template #header>
                    <div class="flex w-4/5">
                        <h3 class="w-1/2 m-0 font-size-16">
                            {{ $t('manage_school_users_invite_table_header_email') }}
                        </h3>
                        <h3 class="w-1/2 m-0 font-size-16 text-center">
                            {{ $t('manage_school_users_invite_table_header_status') }}
                        </h3>
                    </div>
                    <h3 class="w-1/5 m-0 font-size-16 text-right">
                        {{ $t('manage_school_users_invite_table_header_actions') }}
                    </h3>
                </template>
                <template v-slot="{ item }">
                    <div class="flex flex-col md:flex-row justify-between w-full">
                        <div class="w-full md:w-4/5 flex flex-col xl:flex-row xl:items-center">
                            <div class="xl:w-1/2 truncate">
                                {{ item.email }}
                            </div>
                            <div class="w-full md:w-1/2 text-sm xl:text-center">
                                <span class="xl:hidden">{{ $t('manage_school_users_table_status') }}: </span>
                                <span
                                    :class="{
                                        'font-bold': true,
                                        'text-red': [true, false].includes(item.sent) === false,
                                        'text-green': item.sent === true,
                                        'text-grey': item.sent === false
                                    }"
                                >
                                    {{ getSendStatus(item) }}
                                </span>
                            </div>
                        </div>

                        <div class="w-full md:w-1/5 flex md:justify-end">
                            <div class="flex items-center w-full md:w-auto">
                                <XButton
                                    v-if="!item.sent"
                                    v-tippy="{ placement: 'top' }"
                                    @click="remove(item)"
                                    :content="$t('manage_school_users_invite_table_tippy_remove')"
                                    :disabled="loading"
                                    class="flex-1 shadow-text-none"
                                    bg-color="#dc3545"
                                >
                                    <i class="fas fa-times"></i>
                                </XButton>
                            </div>
                        </div>
                    </div>
                </template>
            </ManageTable>
            <XButton
                @click="send"
                :disabled="
                    !inviteUsers.length ||
                    !inviteUsers.filter(user => !user.sent).length ||
                    this.inviteUsers.length > this.limitEmail ||
                    loading
                "
                class="manage-send-button system-button-color system-button-text-color w-full md:w-auto shadow-text-none"
            >
                <span v-if="loading" class="close-loader"></span>
                <i v-else class="fas fa-paper-plane"></i>
                {{ $t('manage_school_users_invite_button_send') }} ({{ inviteUsers.length }}/{{ limitEmail }})
            </XButton>
        </div>
    </div>
</template>

<script>
import { load } from 'recaptcha-v3'
import helperMixins from '@/mixins/helper'
import UserService from '@/lib/user'
import ManageTable from '@/components/manage-school/ManageTable'
import XButton from '@/components/v2/XButton'

export default {
    components: {
        ManageTable,
        XButton
    },

    mixins: [helperMixins],

    data() {
        return {
            email: '',
            inviteUsers: [],
            loading: false,
            alreadyInvited: false,
            limitEmail: 10
        }
    },

    computed: {
        you() {
            const userService = new UserService()

            return userService.getUser()
        },

        emailSyntax() {
            return this.validateEmail(this.email)
        }
    },

    methods: {
        copyLink() {
            this.toastSuccess(this.$t('manage_school_users_invite_popup_copy_link_complete'))
        },

        add() {
            if (!this.emailSyntax || this.loading || this.inviteUsers.length >= this.limitEmail) return

            this.email = this.email.trim().replace(/\s+/g, ' ')

            if (
                this.inviteUsers.find(user => user.email.toLowerCase() === this.email.toLowerCase()) ||
                this.email === this.you.email.toLowerCase()
            ) {
                this.alreadyInvited = true
                return
            }

            this.alreadyInvited = false

            this.inviteUsers.unshift({
                email: this.email,
                sent: false
            })

            this.email = ''
        },

        remove(item) {
            if (item.sent || this.loading) return

            this.inviteUsers.splice(
                this.inviteUsers.findIndex(user => user.email === item.email),
                1
            )
        },

        async send() {
            if (
                !this.inviteUsers.length ||
                !this.inviteUsers.filter(user => !user.sent).length ||
                this.inviteUsers.length > this.limitEmail ||
                this.loading
            ) {
                return
            }

            this.loading = true

            try {
                const recaptcha = await load(process.env.VUE_APP_RECAPTCHA_SITE_KEY, {
                    useRecaptchaNet: true,
                    autoHideBadge: true
                })

                const token = await recaptcha.execute('validate_captcha', {
                    action: 'validate_captcha'
                })

                const sendingEmail = this.inviteUsers.filter(user => !user.sent)

                const payload = {
                    emails: sendingEmail.map(user => {
                        return user.email
                    }),

                    captcha_response: token
                }

                const response = await this.$store.dispatch('inviteUsers', payload)
                const existsInSchoolEmails = response.exists_in_school

                this.inviteUsers = this.inviteUsers.map(user => {
                    if (user.sent === 'exists') {
                        return user
                    }

                    return {
                        ...user,
                        sent: existsInSchoolEmails.includes(user.email) ? 'exists' : true
                    }
                })

                if (existsInSchoolEmails.length === this.inviteUsers.length) {
                    this.toastError(this.$t('manage_school_users_invite_email_all_exists'))
                    return
                }

                this.toastSuccess(this.$t('manage_school_users_invite_popup_invite_complete'))
            } catch {
                this.toastError(this.$t('manage_school_popup_error'))
            } finally {
                this.loading = false
            }
        },

        getSendStatus(item) {
            if (item.sent === true) {
                return this.$t('manage_school_users_invite_table_sent')
            } else if (item.sent === 'exists') {
                return this.$t('manage_school_users_invite_table_exists')
            } else {
                return this.$t('manage_school_users_invite_table_ready_to_send')
            }
        }
    }
}
</script>

<style lang="scss">
#manage-invite-user {
    row-gap: 1rem;
    color: #333;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.5);

    .manage-backdrop {
        background-color: rgba(0, 0, 0, 0.3);
    }

    .manage-close-button {
        width: 1.5rem;
        height: 1.5rem;
    }

    .manage-invite-content {
        row-gap: 1rem;

        .manage-invite-input-box {
            row-gap: 1rem;

            input {
                height: 40px;
                border-radius: 0.5rem;
                padding: 10.5px;
                border: 1px solid #ced4da;
                font-size: 14px;
                color: #333;

                @media screen and (min-width: 768px) {
                    border-radius: 0.5rem 0 0 0.5rem;
                }
            }
        }

        .manage-table-item {
            padding: 0.5rem 1rem;

            @media screen and (min-width: 1200px) {
                min-height: 3.125rem;
            }

            > div {
                row-gap: 1rem;
            }

            .manage-invite-status {
                min-width: 4rem;
            }

            button {
                width: 100%;
                height: 2rem;

                @media screen and (min-width: 768px) {
                    width: 2rem;
                    height: auto;
                    padding: 0;
                    aspect-ratio: 1 / 1;
                }
            }
        }

        .manage-invite-list {
            border: 1px solid #ced4da;
            border-radius: 6px;
        }
    }

    .manage-invite-button {
        border-radius: 0.5rem;
        white-space: nowrap;

        @media screen and (min-width: 768px) {
            border-radius: 0 0.5rem 0.5rem 0 !important;
        }
    }
}
</style>
